import { type ReactElement, isValidElement } from "react"
import type * as React from "react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useMediaQuery } from "@/hooks/use-media-query"
import { cn } from "@/lib/utils"

/**
 * Crew responsive Dialog/Sheet pair. Renders a centered `<Dialog>`
 * at ≥640px (the shadcn `sm:` breakpoint) and a full-height
 * `<Sheet side="bottom">` at <640px. Open/close, focus management,
 * and ESC/overlay behaviour are passthrough — the wrapper holds no
 * state of its own.
 *
 * **Binding testid contract (TRD §2):** when consumers pass
 * `data-testid` on `ResponsiveDialogContent`, the wrapper forwards it
 * to whichever inner Content node renders (DialogContent in desktop,
 * SheetContent in mobile). Exactly one node in the DOM carries that
 * testid at a time — never duplicated across modes, never absent in
 * either. Existing locked QA testids (`*-trigger`, `*-dialog`,
 * `*-submit`) keep resolving across both modes with zero per-mode logic.
 *
 * **Why outside `/ui`:** `/ui/{dialog,sheet}.tsx` are Base UI primitives
 * kept close to the shadcn recipe. This wrapper is a Crew composition over
 * them, not a primitive, so it lives at
 * `app/frontend/components/responsive-dialog.tsx` per TRD §2 + PRD Open
 * Q #6. (`npx shadcn add` is banned — see `docs/frontend/README.md` rule 6.)
 *
 * **Hydration:** the matchMedia query (`(max-width: 639px)`) returns
 * `false` during SSR (see `use-media-query.ts`). The first client
 * render after hydration re-evaluates; users actually on <640px will
 * see a brief hydration-mismatch warning before the wrapper swaps
 * to Sheet mode. This is the same trade-off `useIsMobile` carries
 * across the codebase today — accepted per PRD §7 Open Q #5.
 *
 * **Mid-open viewport flip:** the wrapper subscribes to matchMedia, so
 * resizing across 640px while the dialog is open swaps modes. Field
 * values + processing state are owned by the per-dialog `useState` +
 * Inertia `<Form>` parent (both above the conditional Content branch),
 * so they're preserved across the swap. Best-effort — the minimum bar
 * is "correct on next open".
 */

// 640px = shadcn `sm:` breakpoint. The wrapper renders Sheet mode strictly
// below this width.
const MOBILE_MEDIA_QUERY = "(max-width: 639px)"

// Mixed-library window (OKS-615): the desktop branch renders the migrated Base
// UI `<Dialog>` while the mobile branch still renders the Radix `<Sheet>` (sheet
// migrates in a later ticket). The two libraries' prop surfaces diverge — Base
// UI's `onOpenChange` carries a second `eventDetails` arg and its parts use the
// `render` prop instead of Radix `asChild`. This wrapper exposes ONE stable
// consumer API (the Radix-shaped `asChild` + `(open) => void` surface all 8
// consumers already use) and translates internally per branch, so no consumer
// changes across the mixed-lib window.
interface ResponsiveDialogRootProps {
  open?: boolean
  defaultOpen?: boolean
  /** Narrow `(open) => void` — assignable to both Radix and Base UI roots. */
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  children?: React.ReactNode
}

/**
 * Root component — passthrough for `open` / `onOpenChange` / `children`.
 * Branches internally on the 640px media query.
 */
function ResponsiveDialog(props: ResponsiveDialogRootProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY)
  // Branching the Root (not just the Content) ensures portal trees don't leak
  // across modes mid-open and that `onOpenChange` is wired to the right
  // primitive. The slot subcomponents below read the same media query and
  // render the matching shadcn slot.
  return isMobile ? <Sheet {...props} /> : <Dialog {...props} />
}

type ResponsiveDialogTriggerProps = {
  asChild?: boolean
  children?: React.ReactNode
} & Omit<React.ComponentProps<"button">, "children">

/**
 * Trigger — picks the Base UI Sheet (mobile) or Dialog (desktop) trigger.
 * Since OKS-615 migrated the Sheet to Base UI, BOTH primitives share the same
 * `render`-prop composition (Base UI has no `asChild`). Consumers keep passing
 * `<ResponsiveDialogTrigger asChild>` with a single element child (typically a
 * `<Button>`); that element is forwarded to the primitive's `render` prop.
 */
function ResponsiveDialogTrigger({
  asChild,
  children,
  ...props
}: ResponsiveDialogTriggerProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY)
  if (isMobile) {
    if (asChild && isValidElement(children)) {
      return <SheetTrigger render={children as ReactElement} {...props} />
    }
    return <SheetTrigger {...props}>{children}</SheetTrigger>
  }
  if (asChild && isValidElement(children)) {
    return <DialogTrigger render={children as ReactElement} {...props} />
  }
  return <DialogTrigger {...props}>{children}</DialogTrigger>
}

type ResponsiveDialogContentProps = React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}

/**
 * Content — forwards `data-testid` + `className` to the rendered
 * inner Content node. Mobile mode pins the Sheet to full viewport
 * height (`inset-0 h-svh flex flex-col p-0`) so the per-dialog form
 * body can scroll while the header/footer stick. Desktop mode renders
 * the centered Dialog unchanged.
 *
 * The `showCloseButton` prop is desktop-only (`DialogContent`-specific);
 * mobile mode inherits the built-in `SheetContent` close X (sticky in
 * the top-right of the sheet).
 */
function ResponsiveDialogContent({
  className,
  children,
  showCloseButton,
  ...props
}: ResponsiveDialogContentProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY)
  if (isMobile) {
    // SheetContent defaults to `h-auto` for `side="bottom"`; we override
    // to full-svh height so the form can scroll inside while header/footer
    // stay pinned. `p-0` because the responsive header/footer slots own
    // their own padding (border + sticky positioning depends on it).
    // `showCloseButton` is desktop-only — mobile inherits SheetContent's own X.
    return (
      <SheetContent
        side="bottom"
        className={cn(
          "inset-0 flex h-svh flex-col gap-0 p-0",
          // Allow per-dialog overrides (rare — most callers don't pass
          // a className that needs to apply to the mobile mode).
          className,
        )}
        {...props}
      >
        {children}
      </SheetContent>
    )
  }
  return (
    <DialogContent
      className={className}
      showCloseButton={showCloseButton}
      {...props}
    >
      {children}
    </DialogContent>
  )
}

type ResponsiveDialogHeaderProps = React.ComponentProps<"div">

/**
 * Header — sticky in mobile mode (top of the sheet with a bottom border),
 * standard inline header in desktop mode. The mobile sticky positioning
 * relies on the parent SheetContent being a flex column with `h-svh`.
 */
function ResponsiveDialogHeader({
  className,
  ...props
}: ResponsiveDialogHeaderProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY)
  if (isMobile) {
    return (
      <SheetHeader
        className={cn(
          // Sticky top with solid background so scrolled form content
          // doesn't bleed through; bottom border separates visually.
          "bg-background sticky top-0 z-10 border-b",
          className,
        )}
        {...props}
      />
    )
  }
  return <DialogHeader className={className} {...props} />
}

// h2/p DOM-prop subset is the common denominator across the Base UI Dialog
// (renders `<h2>`/`<p>`) and Radix Sheet titles/descriptions.
type ResponsiveDialogTitleProps = React.ComponentProps<"h2">

function ResponsiveDialogTitle(props: ResponsiveDialogTitleProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY)
  return isMobile ? <SheetTitle {...props} /> : <DialogTitle {...props} />
}

type ResponsiveDialogDescriptionProps = React.ComponentProps<"p">

function ResponsiveDialogDescription(props: ResponsiveDialogDescriptionProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY)
  return isMobile ? (
    <SheetDescription {...props} />
  ) : (
    <DialogDescription {...props} />
  )
}

type ResponsiveDialogFooterProps = React.ComponentProps<"div">

/**
 * Footer — mobile sticks to the bottom of the sheet with a top border;
 * the Cancel→Submit JSX order renders Cancel-on-top per UX Spec §2.
 * Desktop inherits shadcn's `DialogFooter` (`flex-col-reverse` collapses
 * to `sm:flex-row sm:justify-end` at ≥640px → horizontal right-aligned).
 *
 * The wrapper renders Dialog only at ≥640px, so the `flex-col-reverse`
 * narrow-Dialog behavior never triggers — footer is always horizontal
 * in desktop mode. Visual parity with the pre-OKS-120 implementations.
 */
function ResponsiveDialogFooter({
  className,
  ...props
}: ResponsiveDialogFooterProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY)
  if (isMobile) {
    return (
      <SheetFooter
        className={cn(
          // `mt-auto` pushes us to the bottom inside the flex-column
          // SheetContent. Sticky + border-top gives the keyboard-safe
          // pinning per UX Spec §5.
          "bg-background sticky bottom-0 mt-auto border-t",
          // UX Spec §5 — touch targets ≥44px on Cancel + Submit in Sheet
          // mode. shadcn `<Button>` defaults to `h-9` (36px), so a
          // descendant min-height shim bumps every footer button to the
          // WCAG/UX-Spec floor without touching desktop visuals. `min-h-11`
          // = 44px. Selector is intentionally scoped to direct + nested
          // buttons inside the footer so consumers don't need to thread a
          // mobile-aware `size` prop through every dialog. (LSE OKS-120.)
          "[&_button]:min-h-11",
          className,
        )}
        {...props}
      />
    )
  }
  return <DialogFooter className={className} {...props} />
}

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
}
